import React from "react";

/* ============================================================
   LEVELUP ONE — Hand-crafted SVG illustrations & decorations
   Vector art (indigo/violet) — crisp at any size, no external assets
   ============================================================ */

/* shared gradient defs (rendered once per SVG that needs them) */
const Defs = ({ id }) => (
  <defs>
    <linearGradient id={`${id}-grad`} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#4f46e5" />
      <stop offset="100%" stopColor="#7c3aed" />
    </linearGradient>
    <linearGradient id={`${id}-amber`} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#fbbf24" />
      <stop offset="100%" stopColor="#f59e0b" />
    </linearGradient>
    <linearGradient id={`${id}-sky`} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#38bdf8" />
      <stop offset="100%" stopColor="#3b82f6" />
    </linearGradient>
  </defs>
);

/* ---------- Decorative gradient blob ---------- */
export const Blob = ({ className = "", style }) => (
  <svg className={className} style={style} viewBox="0 0 600 600" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="blob-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#6366f1" />
        <stop offset="100%" stopColor="#a855f7" />
      </linearGradient>
    </defs>
    <path fill="url(#blob-g)" d="M421,301Q399,402,300,438Q201,474,128,387Q55,300,123,206Q191,112,295,99Q399,86,425,193Q451,300,421,301Z" />
  </svg>
);

/* ---------- Dotted grid texture ---------- */
export const DottedGrid = ({ className = "", style }) => (
  <svg className={className} style={style} width="220" height="220" viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <pattern id="dots" width="22" height="22" patternUnits="userSpaceOnUse">
        <circle cx="2" cy="2" r="2" fill="#c7d2fe" />
      </pattern>
    </defs>
    <rect width="220" height="220" fill="url(#dots)" />
  </svg>
);

/* ---------- Hero: AI hub connecting systems ---------- */
export const HeroConnectArt = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 520 460" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="AI เชื่อมต่อระบบเดิม">
    <Defs id="hero" />
    {/* connection lines */}
    <g stroke="#c7d2fe" strokeWidth="2.5" strokeDasharray="6 7" fill="none">
      <path d="M260 230 L110 120" />
      <path d="M260 230 L410 120" />
      <path d="M260 230 L110 350" />
      <path d="M260 230 L410 350" />
    </g>
    {/* central AI core */}
    <circle cx="260" cy="230" r="74" fill="url(#hero-grad)" />
    <circle cx="260" cy="230" r="74" fill="none" stroke="#ffffff" strokeOpacity="0.4" strokeWidth="6" />
    <text x="260" y="244" textAnchor="middle" fontSize="40" fontWeight="800" fill="#ffffff" fontFamily="Plus Jakarta Sans, sans-serif">AI</text>
    {/* node: POS */}
    <g transform="translate(60 70)">
      <rect width="100" height="100" rx="22" fill="#ffffff" stroke="#e0e7ff" strokeWidth="2" />
      <rect x="26" y="26" width="48" height="34" rx="5" fill="url(#hero-grad)" />
      <rect x="32" y="64" width="36" height="8" rx="4" fill="#c7d2fe" />
    </g>
    {/* node: chat phone */}
    <g transform="translate(360 70)">
      <rect width="100" height="100" rx="22" fill="#ffffff" stroke="#e0e7ff" strokeWidth="2" />
      <rect x="34" y="22" width="32" height="56" rx="8" fill="url(#hero-sky)" />
      <rect x="40" y="32" width="20" height="6" rx="3" fill="#ffffff" opacity="0.85" />
      <rect x="40" y="44" width="14" height="6" rx="3" fill="#ffffff" opacity="0.7" />
    </g>
    {/* node: spreadsheet */}
    <g transform="translate(60 300)">
      <rect width="100" height="100" rx="22" fill="#ffffff" stroke="#e0e7ff" strokeWidth="2" />
      <rect x="24" y="26" width="52" height="48" rx="6" fill="#dcfce7" />
      <g stroke="#86efac" strokeWidth="3"><path d="M24 44h52M24 60h52M50 26v48" /></g>
    </g>
    {/* node: chart */}
    <g transform="translate(360 300)">
      <rect width="100" height="100" rx="22" fill="#ffffff" stroke="#e0e7ff" strokeWidth="2" />
      <rect x="28" y="56" width="12" height="22" rx="3" fill="url(#hero-amber)" />
      <rect x="44" y="42" width="12" height="36" rx="3" fill="url(#hero-grad)" />
      <rect x="60" y="30" width="12" height="48" rx="3" fill="url(#hero-sky)" />
    </g>
    {/* floating sparkles */}
    <circle cx="260" cy="120" r="6" fill="#fbbf24" />
    <circle cx="170" cy="240" r="5" fill="#a855f7" />
    <circle cx="350" cy="240" r="5" fill="#38bdf8" />
  </svg>
);

/* ---------- ERP dashboard mockup ---------- */
export const DashboardArt = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 560 380" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="แดชบอร์ดระบบ ERP">
    <Defs id="dash" />
    <rect x="6" y="6" width="548" height="368" rx="20" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2" />
    {/* top bar */}
    <rect x="6" y="6" width="548" height="46" rx="20" fill="#f8fafc" />
    <circle cx="34" cy="29" r="5" fill="#f87171" />
    <circle cx="52" cy="29" r="5" fill="#fbbf24" />
    <circle cx="70" cy="29" r="5" fill="#34d399" />
    <rect x="360" y="20" width="170" height="18" rx="9" fill="#eef2ff" />
    {/* sidebar */}
    <rect x="22" y="68" width="120" height="290" rx="14" fill="#0f172a" />
    <rect x="38" y="92" width="88" height="12" rx="6" fill="url(#dash-grad)" />
    {[124, 152, 180, 208].map((y, i) => (
      <rect key={i} x="38" y={y} width={i === 1 ? 70 : 88} height="10" rx="5" fill="#334155" />
    ))}
    {/* KPI cards */}
    {[0, 1, 2].map((i) => (
      <g key={i} transform={`translate(${162 + i * 132} 72)`}>
        <rect width="118" height="78" rx="14" fill="#f8fafc" stroke="#e2e8f0" />
        <rect x="16" y="18" width="48" height="10" rx="5" fill="#cbd5e1" />
        <rect x="16" y="38" width="70" height="18" rx="6" fill={i === 0 ? "url(#dash-grad)" : i === 1 ? "url(#dash-amber)" : "url(#dash-sky)"} />
      </g>
    ))}
    {/* line chart */}
    <g transform="translate(162 168)">
      <rect width="250" height="178" rx="14" fill="#f8fafc" stroke="#e2e8f0" />
      <polyline points="20,130 60,90 100,108 140,60 180,80 220,36" fill="none" stroke="url(#dash-grad)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="20,150 60,140 100,120 140,128 180,96 220,104" fill="none" stroke="#38bdf8" strokeWidth="3.5" strokeDasharray="5 6" strokeLinecap="round" />
    </g>
    {/* donut */}
    <g transform="translate(426 168)">
      <rect width="116" height="178" rx="14" fill="#f8fafc" stroke="#e2e8f0" />
      <circle cx="58" cy="74" r="34" fill="none" stroke="#e0e7ff" strokeWidth="14" />
      <circle cx="58" cy="74" r="34" fill="none" stroke="url(#dash-grad)" strokeWidth="14" strokeDasharray="150 214" strokeLinecap="round" transform="rotate(-90 58 74)" />
      <rect x="20" y="128" width="76" height="9" rx="4" fill="#cbd5e1" />
      <rect x="20" y="146" width="56" height="9" rx="4" fill="#e2e8f0" />
    </g>
  </svg>
);

/* ---------- Friendly robot mascot ---------- */
const BOT_POSES = ["jump", "wiggle", "nod", "cheer", "excited"];

export const MascotBot = ({ className = "", style }) => {
  const [pose, setPose] = React.useState("");
  React.useEffect(() => {
    let alive = true;
    let t1, t2;
    const loop = () => {
      const gap = 2600 + Math.random() * 3200; // ช่วงพักก่อนเล่นท่าถัดไป
      t1 = setTimeout(() => {
        if (!alive) return;
        const p = BOT_POSES[Math.floor(Math.random() * BOT_POSES.length)];
        setPose(p);
        t2 = setTimeout(() => { if (alive) { setPose(""); loop(); } }, 1400);
      }, gap);
    };
    loop();
    return () => { alive = false; clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
  <svg className={`nx-mascot ${className}`} style={style} viewBox="0 0 260 300" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="ผู้ช่วย AI น้องเลเวล">
    <Defs id="bot" />
    <g className={`nx-bot-pose ${pose ? "is-" + pose : ""}`}>
    <g className="nx-bot">
      {/* shadow */}
      <ellipse className="nx-bot__shadow" cx="130" cy="286" rx="58" ry="9" fill="#0f172a" opacity="0.12" />
      {/* antenna */}
      <line x1="130" y1="38" x2="130" y2="64" stroke="#7c3aed" strokeWidth="6" strokeLinecap="round" />
      <circle className="nx-bot__antenna" cx="130" cy="32" r="9" fill="url(#bot-amber)" />
      {/* head (tilts) */}
      <g className="nx-bot__head">
        <rect x="62" y="62" width="136" height="112" rx="34" fill="url(#bot-grad)" />
        <rect x="80" y="92" width="100" height="56" rx="22" fill="#0f172a" />
        <g className="nx-bot__eyes">
          <circle cx="112" cy="120" r="11" fill="#38bdf8" />
          <circle cx="148" cy="120" r="11" fill="#38bdf8" />
          <circle cx="115" cy="117" r="3.5" fill="#ffffff" />
          <circle cx="151" cy="117" r="3.5" fill="#ffffff" />
        </g>
        {/* cheeks */}
        <circle cx="86" cy="138" r="6" fill="#f9a8d4" opacity="0.8" />
        <circle cx="174" cy="138" r="6" fill="#f9a8d4" opacity="0.8" />
      </g>
      {/* body */}
      <rect x="78" y="182" width="104" height="86" rx="26" fill="#ffffff" stroke="#e0e7ff" strokeWidth="3" />
      <circle cx="130" cy="214" r="16" fill="url(#bot-grad)" />
      <rect x="104" y="240" width="52" height="10" rx="5" fill="#e0e7ff" />
      {/* static arm (right) */}
      <rect x="200" y="190" width="20" height="56" rx="10" fill="url(#bot-grad)" />
      {/* waving arm (left) */}
      <g className="nx-bot__arm">
        <rect x="40" y="176" width="20" height="56" rx="10" fill="url(#bot-grad)" />
        <circle cx="50" cy="178" r="12" fill="url(#bot-amber)" />
      </g>
    </g>
    </g>
  </svg>
  );
};

/* ---------- Industry scene tile (parametric) ---------- */
export const SceneArt = ({ kind = "retail", className = "", style }) => {
  const scenes = {
    retail: (
      <g>
        <rect x="60" y="70" width="140" height="120" rx="14" fill="#ffffff" stroke="#e0e7ff" strokeWidth="3" />
        <rect x="80" y="92" width="100" height="56" rx="8" fill="url(#scene-grad)" />
        <rect x="92" y="160" width="76" height="12" rx="6" fill="#e0e7ff" />
        <circle cx="210" cy="150" r="26" fill="url(#scene-amber)" />
        <path d="M200 150h20M210 140v20" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
      </g>
    ),
    laundry: (
      <g>
        <rect x="70" y="60" width="120" height="140" rx="16" fill="#ffffff" stroke="#e0e7ff" strokeWidth="3" />
        <circle cx="130" cy="135" r="40" fill="#eef2ff" stroke="url(#scene-grad)" strokeWidth="6" />
        <circle cx="130" cy="135" r="20" fill="url(#scene-sky)" />
        <rect x="86" y="76" width="20" height="14" rx="4" fill="#c7d2fe" />
        <rect x="116" y="76" width="40" height="14" rx="7" fill="url(#scene-amber)" />
      </g>
    ),
    ecommerce: (
      <g>
        <rect x="80" y="70" width="70" height="120" rx="14" fill="#ffffff" stroke="#e0e7ff" strokeWidth="3" />
        <rect x="92" y="84" width="46" height="70" rx="8" fill="url(#scene-grad)" />
        <circle cx="115" cy="172" r="6" fill="#c7d2fe" />
        <path d="M165 110h44l-8 40h-30z" fill="url(#scene-amber)" />
        <circle cx="178" cy="166" r="7" fill="#7c3aed" />
        <circle cx="200" cy="166" r="7" fill="#7c3aed" />
      </g>
    ),
    team: (
      <g>
        <circle cx="105" cy="110" r="26" fill="url(#scene-grad)" />
        <rect x="79" y="142" width="52" height="40" rx="14" fill="url(#scene-grad)" />
        <circle cx="170" cy="118" r="22" fill="url(#scene-sky)" />
        <rect x="148" y="146" width="44" height="36" rx="12" fill="url(#scene-sky)" />
        <circle cx="215" cy="128" r="16" fill="url(#scene-amber)" />
      </g>
    ),
    chat: (
      <g>
        <rect x="70" y="80" width="110" height="70" rx="18" fill="url(#scene-grad)" />
        <path d="M96 150l-6 22 26-14z" fill="url(#scene-grad)" />
        <rect x="88" y="102" width="60" height="9" rx="4" fill="#fff" opacity="0.9" />
        <rect x="88" y="120" width="40" height="9" rx="4" fill="#fff" opacity="0.7" />
        <rect x="150" y="135" width="80" height="50" rx="16" fill="#ffffff" stroke="#e0e7ff" strokeWidth="3" />
        <rect x="164" y="150" width="50" height="8" rx="4" fill="#e0e7ff" />
        <rect x="164" y="166" width="34" height="8" rx="4" fill="#e0e7ff" />
      </g>
    ),
  };
  return (
    <svg className={className} style={style} viewBox="0 0 280 230" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <Defs id="scene" />
      <rect width="280" height="230" rx="20" fill="#f5f3ff" />
      {scenes[kind] || scenes.retail}
    </svg>
  );
};

/* ---------- Wave section divider ---------- */
export const WaveDivider = ({ flip, color = "#f8fafc", className = "" }) => (
  <svg className={className} viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
       style={{ display: "block", width: "100%", height: 60, transform: flip ? "rotate(180deg)" : undefined }}>
    <path fill={color} d="M0,40 C360,90 720,0 1080,30 C1260,45 1380,55 1440,50 L1440,80 L0,80 Z" />
  </svg>
);
